import parsePhoneNumber, { CountryCode } from 'libphonenumber-js';

import { format, clean, validate } from 'rut.js';

import { store } from '@/ui/store/store';

import ClientRepository from '@/data/repositories/ClientRepository';

import { IUserResponse } from '@/domain/interfaces/ILoginResponse';
import { IClient } from '@/domain/interfaces/IUser';
import { IStore } from '@/domain/interfaces/IStore';

import useEmailValidation from '@/domain/genericValidations/useEmailValidation';
import useNameValidation from '@/domain/genericValidations/useNameValidation';
import useDateValidation from '@/domain/genericValidations/useDateValidation';

import { VALIDATE_EMAIL } from '@/data/libraries/ConfigParameters';
import { IdentificationTypes } from '@/data/libraries/IdentificationTypes';
import { ALPHANUMERIC_WITH_DASH, ALPHANUMERIC_WITHOUT_SPECIAL_CHARACTER } from '@/data/libraries/ConfigParameters';
import validatePattern from '@/domain/utils/validatePattern';
import { translate } from '@/domain/utils/translate';

/**
 * Caso de uso de registro de nuevo cliente - Validaciones
 * 
 * @param {IClient} user - data para nuevo registro
 * @param {string} token - token valido
 * @returns {IUserResponse} - usuario registrado
 */
const updateUser = async (user: IClient, id: number, token: string, toSend: boolean): Promise<IUserResponse> => {

  const messageNoExist: string = translate('session.message_no_exist')
  /**
   * Instancia del Repository
   * @const {ClientRepository}
   */
  const clientRepository = ClientRepository.instance;

  /** Hooks de validaciones genericas */
  const validationEmail = useEmailValidation;
  const validationNames = useNameValidation;
  const validationDate = useDateValidation;

  /** Estado redux de tienda */
  const storeData = store.getState().storeData as IStore;

  /** Objeto enviado en el return */
  let response: IUserResponse = {
    data: null,
    isError: true,
    inputError: null,
    message: []
  };

  /** Almacenara el codigo de error en repository si existe */
  let errorCatch: number | string | null = null;

  /** Almacenara el mensaje de error en repository si existe */
  let messageErrorCatch: string = '';

  /** Mensajes de error */
  const EMAIL_ERROR: string = translate('user.email_error');
  const NAME_ERROR: string = translate('user.name_error');
  const DATE_ERROR: string = translate('user.date_error');
  const IDENTIFICATION_FORMAT_ERROR: string = translate('user.invalid_format_id');
  const CLIENT_ID_REQUIRED: string = translate('user.required_id');
  const ID_TYPE_NO_VALID: string = translate('user.invalid_id');
  const PHONE_ERROR: string = translate('user.invalid_phone');

  /** Codigo E1006 entregado por el back con mensaje customisado para el usuario*/
  const BACK_ERROR_E1006: string = 'E1006';
  const BACK_E1006_MESSAGE: string = translate('user.e1006_error');

  /** Codigo E1002 entregado por el back con mensaje error */
  const BACK_ERROR_E1002: string = 'E1002';
  const BACK_E1002_MESSAGE: string = translate('user.e1002_error');

  /** Codigo E1008 entregado por el back con mensaje customisado para el usuario*/
  const BACK_ERROR_E1008: string = 'E1008';
  const BACK_E1008_MESSAGE: string = translate('user.e1002_error');

  /** Validación  email */
  if (user.email !== undefined) {
    if (user.email === null || user.email.length === 0) {
      response.inputError = 'email';
      response.message.push(EMAIL_ERROR);
      return response;
    }
    if (!validationEmail(user.email) || !validatePattern(VALIDATE_EMAIL, user.email)) {
      response.inputError = 'email';
      response.message.push(EMAIL_ERROR);
      return response;
    } else {
      user.email = user.email?.toLocaleLowerCase();
    }
  }

  /** Validación  nombre */
  if (user.firstName !== undefined) {
    if (user.firstName === null || user.firstName.length === 0) {
      response.inputError = 'name';
      response.message.push(NAME_ERROR);
      return response;
    }
    if (!validationNames(user.firstName!) || user.firstName.length === 0) {
      response.inputError = 'name';
      response.message.push(NAME_ERROR);
      return response;
    }
  }

  /** Validación  apellido */
  if (user.lastName !== undefined) {
    if (user.lastName === null || user.lastName.length === 0) {
      response.inputError = 'lastName';
      response.message.push(NAME_ERROR);
      return response;
    }
    if (!validationNames(user.lastName!)  || user.lastName.length === 0) {
      response.inputError = 'lastName';
      response.message.push(NAME_ERROR);
      return response;
    }
  }

  /** Validación  fecha nacimiento */
  if (user.birthDay) {
    if (!validationDate(user.birthDay!)) {
      response.inputError = 'birthday';
      response.message.push(DATE_ERROR);
      return response;
    }
  }

  /** Validación  identificacion oficial */

  
    /** Inicio Validación Tipo de Documento */
    if (user.idType !== undefined || user.identification !== undefined){
      if (!(user.idType === '0' && user.identification === '')) {
        if (user.idType !== '' && user.identification === '') {
          response.inputError = 'identification';
          response.message.push(ID_TYPE_NO_VALID);
          return response;
        } else if ((user.idType === undefined || user.idType === '0') && user.identification !== '') {
          response.inputError = 'identification';
          response.message.push(ID_TYPE_NO_VALID);
          return response;
        } else if (
          user.idType === IdentificationTypes.RUT_TYPE.toString() &&
          (!validate(user.identification ?? '') || clean(user.identification ?? '').length < 9)
        ) {
          response.inputError = 'identification';
          response.message.push(IDENTIFICATION_FORMAT_ERROR);
          return response;
        } else if (
          user.idType === IdentificationTypes.RUT_TYPE.toString() &&
          !validatePattern(ALPHANUMERIC_WITH_DASH, user.identification ?? '')
        ) {
          response.inputError = 'identification';
          response.message.push(IDENTIFICATION_FORMAT_ERROR);
          return response;
        } else if (
          (user.idType === IdentificationTypes.PASSPORT_TYPE.toString() ||
          user.idType === IdentificationTypes.CC_TYPE.toString()) &&
          !validatePattern(ALPHANUMERIC_WITHOUT_SPECIAL_CHARACTER, user.identification ?? '')
        ) {
          response.inputError = 'identification';
          response.message.push(IDENTIFICATION_FORMAT_ERROR);
          return response;
        }
        //validar que el rut no exceda los 9 digitos
        if (user.idType === IdentificationTypes.RUT_TYPE.toString() && clean(user.identification ?? '').length > 9) {
          response.inputError = 'identification';
          response.message.push(IDENTIFICATION_FORMAT_ERROR);
          return response;
        }
      } else {
        if (storeData.purchaseOnlyClient) {
          response.inputError = 'identification';
          response.message.push(CLIENT_ID_REQUIRED);
          return response;
        }
      }
    } /** Fin Validación Tipo de Documento */



  /** Validación teléfono */
  if (user.phone !== undefined) {
    if (user.phone === null) {
      response.inputError = 'phone';
      response.message.push(PHONE_ERROR);
      return response;
    } 
    if (!user.phone.number || user.phone.number === '' || !user.phone.country || user.phone.country === '') {
      response.inputError = 'phone';
      response.message.push(PHONE_ERROR);
      return response;
    } else {
      if ((user.phone.country === 'CL' && user.phone.number.length - 2 !== 9) || !user.phone.number.startsWith('569')) {
        response.inputError = 'phone';
        response.message.push(PHONE_ERROR);
        return response;
      } else {
        const parsedNumber = parsePhoneNumber(user.phone.number, user.phone.country as CountryCode);
  
        if (!parsedNumber || !parsedNumber.isValid()) {
          response.inputError = 'phone';
          response.message.push(PHONE_ERROR);
          return response;
        }
      }
    }
    
  }

  /** Si pasa las validaciones apaga el error flag */
  response.isError = false;

  /**
   * Registra el cliente si es un submit action de lo contrario solo valida
   */
  try {

    /** Repository de Cliente */
    if (user.idType === IdentificationTypes.RUT_TYPE.toString()) {
      user.identification = format(user.identification ?? '', { dots: false });
    }
    
    if (toSend) {
      await clientRepository.updateClient(user, id, token)
        .then((result) => {
          if (result.status === 200) {
            /** Apaga el flag de error */
            response.isError = false;
          }
        })
        .catch((error) => {
          /** Devuelve el error */
          errorCatch = error.response.status;
          messageErrorCatch = error.response.data;
        });
    } else {
      /** Repository validación de Cliente */
      await clientRepository.validateUpdateClient(user, id, token)
        .then((result) => {
          if (result.status === 200) {
            /** Apaga el flag de error */
            response.isError = false;
          }
        })
        .catch((error) => {
          /** Devuelve el error */
          errorCatch = error.response.status;
          messageErrorCatch = error.response.data;
        });
    }
      /** Error 204 el usuario no existe */
      if (errorCatch === 204) {
        response.message.push(messageNoExist);
        return response;
      }

      /** Error 422 el usuario no paso validación en back */
      if (errorCatch === 422) {
        response.isError = true;

        if (messageErrorCatch.includes(BACK_ERROR_E1006)) {
          response.message.push(BACK_E1006_MESSAGE);
        } else if (messageErrorCatch.includes(BACK_ERROR_E1008)) {
          response.message.push(BACK_E1008_MESSAGE);
        } else if (messageErrorCatch.includes(BACK_ERROR_E1002)) {
          response.message.push(BACK_E1002_MESSAGE);
        } else {
          response.message.push(messageErrorCatch);
        }

        return response;
      }
    
  } catch (error) {
    console.error(error);
  }
  return response;
}

export default updateUser;